import type { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

const MODEL_URL = '/assets/character/heroine-witch.gltf';
const CHARACTER_SCALE = 1.3;

type LocomotionState = 'idle' | 'jog' | 'sprint' | 'victory' | 'kick';

export interface AdventurerPose {
  moving: boolean;
  sprinting: boolean;
  kickProgress?: number;
  stumbleProgress?: number;
  celebrating: boolean;
}

/**
 * Loads and drives the detailed heroine without coupling gameplay to asset loading.
 * The collision capsule and all combat calculations remain owned by Player.
 */
export class RiggedAdventurer {
  private readonly wrapper: TransformNode;
  private readonly meshes: AbstractMesh[] = [];
  private readonly groups = new Map<string, AnimationGroup>();
  private shadow?: ShadowGenerator;
  private state?: LocomotionState;
  private disposed = false;
  private ready = false;

  constructor(
    private readonly scene: Scene,
    parent: TransformNode,
    private readonly onReady: () => void,
  ) {
    this.wrapper = new TransformNode('rigged-adventurer', scene);
    this.wrapper.parent = parent;
    this.wrapper.position.y = -1.04;
    this.wrapper.scaling.setAll(CHARACTER_SCALE);
    // The heroine's authored +Z front already matches Player's +Z forward.
    this.wrapper.rotation.y = 0;
    this.wrapper.setEnabled(false);
    void this.load();
  }

  get isReady(): boolean { return this.ready; }

  setShadowGenerator(shadow?: ShadowGenerator): void {
    this.shadow = shadow;
    if (!shadow) return;
    this.meshes.forEach((mesh) => shadow.addShadowCaster(mesh, false));
  }

  update(pose: AdventurerPose): void {
    if (!this.ready) return;

    this.wrapper.rotation.x = 0;
    this.wrapper.rotation.y = 0;
    this.wrapper.rotation.z = pose.stumbleProgress !== undefined
      ? Math.sin(pose.stumbleProgress * Math.PI * 3) * (1 - pose.stumbleProgress) * 0.18
      : 0;

    if (pose.kickProgress !== undefined) this.setState('kick');
    else if (pose.celebrating) this.setState('victory');
    else if (pose.moving) this.setState(pose.sprinting ? 'sprint' : 'jog');
    else this.setState('idle');
  }

  dispose(): void {
    this.disposed = true;
    this.groups.forEach((group) => group.dispose());
    this.groups.clear();
    this.wrapper.dispose(false, true);
  }

  private async load(): Promise<void> {
    try {
      await import('@babylonjs/loaders/glTF');
      const { ImportMeshAsync } = await import('@babylonjs/core/Loading/sceneLoader');
      const result = await ImportMeshAsync(MODEL_URL, this.scene);
      if (this.disposed || this.scene.isDisposed) {
        result.meshes.forEach((mesh) => mesh.dispose(false, true));
        result.animationGroups.forEach((group) => group.dispose());
        return;
      }

      const importedRoot = result.meshes[0];
      if (!importedRoot) throw new Error('The heroine model did not contain a root mesh.');
      importedRoot.parent = this.wrapper;
      for (const mesh of result.meshes) {
        this.meshes.push(mesh);
        mesh.isPickable = false;
        mesh.receiveShadows = true;
        this.shadow?.addShadowCaster(mesh, false);
      }

      for (const group of result.animationGroups) {
        group.stop();
        group.enableBlending = true;
        group.blendingSpeed = 0.11;
        this.groups.set(group.name, group);
      }

      this.ready = true;
      this.wrapper.setEnabled(true);
      this.setState('idle');
      this.onReady();
    } catch (error) {
      // The procedural heroine remains visible as a deliberate offline/low-end fallback.
      if (!this.disposed && !this.scene.isDisposed) console.warn('Detailed heroine could not be loaded.', error);
    }
  }

  private setState(next: LocomotionState): void {
    if (this.state === next) return;
    this.state = next;
    this.groups.forEach((group) => group.stop());

    const animationName = {
      idle: 'Idle',
      jog: 'Run',
      sprint: 'Run',
      victory: 'Wave',
      kick: 'Kick_Right',
    }[next];
    const speed = next === 'jog' ? 0.82 : next === 'sprint' ? 1.18 : next === 'kick' ? 1.3 : 1;
    const loop = next !== 'kick';
    this.groups.get(animationName)?.start(loop, speed);
  }
}
